import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth, hasPagePermission } from '../context/AuthContext';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Factory, 
  FlaskConical, 
  ClipboardCheck, 
  ListTodo,
  FileText,
  AlertTriangle,
  ChevronRight,
  Clock,
  User,
  Calendar,
  CheckCircle2,
  XCircle,
  Bell,
  Activity,
  TrendingUp,
  Zap
} from 'lucide-react';
import { 
  formatDateDisplay, 
  formatRelativeTime, 
  getCurrentPragueTime,
  isPastInPrague,
  isTodayInPrague,
  isTomorrowInPrague 
} from '../utils/dateUtils';

export const HomePage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [stats, setStats] = useState({
    pendingTasks: 0,
    todayTests: 0,
    openFindings: 0,
    productionRecords: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch user's tasks
      const tasksPromise = api.get('/tasks/events?status=pending&limit=10').catch(() => ({ data: { events: [] } }));
      
      // Fetch recent activity
      const activityPromise = api.get('/activity/recent?limit=8').catch(() => ({ data: [] }));
      
      // Fetch stats
      const statsPromise = Promise.all([
        api.get('/tasks/events?status=pending&limit=1').catch(() => ({ data: { total: 0 } })),
        api.get('/sensory/tests?limit=1').catch(() => ({ data: { total: 0 } })),
        api.get('/audits/findings?status=open&limit=1').catch(() => ({ data: { total: 0 } })),
        api.get('/production/last-import').catch(() => ({ data: { total_records: 0 } }))
      ]);

      const [tasksRes, activityRes, statsRes] = await Promise.all([
        tasksPromise,
        activityPromise,
        statsPromise
      ]);

      // Flatten events into tasks array
      const events = tasksRes.data.events || [];
      const flatTasks = events.flatMap(e => e.tasks || []);
      setTasks(flatTasks);
      setRecentActivity(activityRes.data || []);
      setStats({
        pendingTasks: statsRes[0].data.total || 0,
        todayTests: statsRes[1].data.pagination?.total || statsRes[1].data.total || 0,
        openFindings: statsRes[2].data.total || 0,
        productionRecords: statsRes[3].data.total_records || 0
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return formatDateDisplay(dateStr);
  };

  const formatRelative = (dateStr) => {
    return formatRelativeTime(dateStr);
  };

  const getTaskPriority = (task) => {
    if (!task.due_date) return 'normal';
    if (isPastInPrague(task.due_date) && !isTodayInPrague(task.due_date)) return 'overdue';
    if (isTodayInPrague(task.due_date)) return 'today';
    if (isTomorrowInPrague(task.due_date)) return 'tomorrow';
    return 'normal';
  };

  // Module cards with icons and colors
  const modules = [
    {
      key: 'production',
      name: 'Výroba',
      description: 'Data z balírny a výroby',
      icon: Factory,
      color: 'bg-indigo-500',
      link: '/vyroba',
      stat: stats.productionRecords,
      statLabel: 'záznamů'
    },
    {
      key: 'sensory',
      name: 'Senzorika',
      description: 'Senzorické zkoušky',
      icon: FlaskConical,
      color: 'bg-purple-500',
      link: '/senzorika/hotovky',
      stat: stats.todayTests,
      statLabel: 'testů'
    },
    {
      key: 'audits',
      name: 'Inspekce',
      description: 'Interní inspekce a nálezy',
      icon: ClipboardCheck,
      color: 'bg-amber-500',
      link: '/audity',
      stat: stats.openFindings,
      statLabel: 'otevřených nálezů'
    },
    {
      key: 'tasks',
      name: 'Úkoly',
      description: 'Správa úkolů a termínů',
      icon: ListTodo,
      color: 'bg-green-500',
      link: '/ukoly',
      stat: stats.pendingTasks,
      statLabel: 'čeká na splnění'
    },
    {
      key: 'reports',
      name: 'Hlášení',
      description: 'Zmetková výroba',
      icon: FileText,
      color: 'bg-red-500',
      link: '/reports'
    }
  ];

  // Filter modules based on user permissions
  const visibleModules = modules.filter(m => {
    if (m.key === 'production') return hasPagePermission(user, 'production', 'view');
    if (m.key === 'sensory') return hasPagePermission(user, 'sensory', 'view');
    if (m.key === 'audits') return true; // Všichni uživatelé mají přístup
    if (m.key === 'tasks') return true; // Všichni uživatelé mají přístup
    if (m.key === 'reports') return hasPagePermission(user, 'reports', 'view');
    return true;
  });

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-slate-800">
            Dobrý den, {user?.name?.split(' ')[0] || 'uživateli'}!
          </h1>
          <p className="text-slate-500 mt-1">
            {getCurrentPragueTime("EEEE, d. MMMM yyyy")}
          </p>
        </div>

        {/* Quick Stats Row */}
        {!loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <ListTodo className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.pendingTasks}</p>
                    <p className="text-xs text-slate-500">Čekajících úkolů</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FlaskConical className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800" data-testid="stat-today-tests">{stats.todayTests}</p>
                    <p className="text-xs text-slate-500">Senzorických testů</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.openFindings}</p>
                    <p className="text-xs text-slate-500">Otevřených nálezů</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-indigo-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <Factory className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.productionRecords.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">Výrobních záznamů</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Access Modules */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Rychlý přístup
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {visibleModules.map((module) => {
                    const Icon = module.icon;
                    return (
                      <Link
                        key={module.key}
                        to={module.link}
                        className="group p-4 rounded-xl border bg-white hover:shadow-lg hover:border-slate-300 transition-all duration-200"
                      >
                        <div className={`w-12 h-12 ${module.color} rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="font-semibold text-slate-800 mb-1">{module.name}</h3>
                        <p className="text-xs text-slate-500 mb-2">{module.description}</p>
                        {module.stat !== undefined && (
                          <div className="flex items-center gap-1 text-xs">
                            <span className="font-medium text-slate-700">{module.stat.toLocaleString()}</span>
                            <span className="text-slate-400">{module.statLabel}</span>
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* My Tasks / Agenda */}
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-green-500" />
                  Moje úkoly
                </CardTitle>
                <Link to="/ukoly">
                  <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
                    Zobrazit vše
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-3 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : tasks.length === 0 ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-12 h-12 text-green-300 mx-auto mb-3" />
                    <p className="text-slate-500">Nemáte žádné čekající úkoly</p>
                    <p className="text-sm text-slate-400">Skvělá práce!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tasks.slice(0, 5).map((task) => {
                      const priority = getTaskPriority(task);
                      return (
                        <Link
                          key={task.id}
                          to={`/ukoly/${task.id}`}
                          className="flex items-center gap-4 p-3 rounded-lg border hover:bg-slate-50 transition-colors"
                        >
                          <div className={`w-2 h-2 rounded-full ${
                            priority === 'overdue' ? 'bg-red-500' :
                            priority === 'today' ? 'bg-amber-500' :
                            priority === 'tomorrow' ? 'bg-blue-500' : 'bg-slate-300'
                          }`} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800 truncate">{task.title}</p>
                            <p className="text-xs text-slate-500 truncate">{task.description}</p>
                          </div>
                          <div className="text-right shrink-0">
                            {task.due_date && (
                              <Badge variant={
                                priority === 'overdue' ? 'destructive' :
                                priority === 'today' ? 'default' : 'secondary'
                              } className="text-xs">
                                {formatDate(task.due_date)}
                              </Badge>
                            )}
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Activity Feed */}
          <div className="space-y-6">
            {/* Recent Activity */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-500" />
                  Poslední aktivita
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : recentActivity.length === 0 ? (
                  <div className="text-center py-8">
                    <Bell className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">Žádná nedávná aktivita</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {recentActivity.map((activity, index) => (
                      <div key={index} className="flex gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                          activity.type === 'task_completed' ? 'bg-green-100' :
                          activity.type === 'finding_added' ? 'bg-amber-100' :
                          activity.type === 'test_created' ? 'bg-purple-100' :
                          activity.type === 'import_completed' ? 'bg-indigo-100' : 'bg-slate-100'
                        }`}>
                          {activity.type === 'task_completed' ? (
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          ) : activity.type === 'finding_added' ? (
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                          ) : activity.type === 'test_created' ? (
                            <FlaskConical className="w-4 h-4 text-purple-600" />
                          ) : activity.type === 'import_completed' ? (
                            <Factory className="w-4 h-4 text-indigo-600" />
                          ) : (
                            <Activity className="w-4 h-4 text-slate-600" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700 line-clamp-2">{activity.message}</p>
                          <p className="text-xs text-slate-400 mt-1">{formatRelative(activity.created_at)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Tips */}
            <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-800 mb-1">Tip dne</h4>
                    <p className="text-sm text-slate-600">
                      Používejte klávesové zkratky pro rychlejší navigaci. 
                      Stiskněte <kbd className="px-1.5 py-0.5 bg-white rounded text-xs font-mono">/</kbd> pro vyhledávání.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HomePage;
